<template>
    <main v-if="!isInstalled" class="install-page">
        <section class="install-card">
            <button class="install-close" type="button" @click="emit('close')">
                ×
            </button>

            <div class="install-logo">
                <img src="/icons/android-chrome.png" alt="ГИС Инвентаризация" />
            </div>

            <h1>ГИС Инвентаризация</h1>

            <p class="install-subtitle">
                Для удобной работы добавьте приложение на главный экран устройства.
            </p>

            <div v-if="platform === 'ios'" class="guide">
                <h2>Инструкция для iPhone / iPad</h2>

                <div class="step">
                    <span>1</span>
                    <p>Откройте сайт в браузере Safari.</p>
                </div>

                <div class="step">
                    <span>2</span>
                    <p>Нажмите кнопку «Поделиться» в нижней части экрана.</p>
                </div>

                <div class="step">
                    <span>3</span>
                    <p>Выберите пункт «На экран Домой».</p>
                </div>

                <div class="step">
                    <span>4</span>
                    <p>Нажмите «Добавить». Иконка появится на главном экране.</p>
                </div>

                <p class="notice">
                    На iOS установка работает только через Safari.
                </p>
            </div>

            <div v-else-if="platform === 'android'" class="guide">
                <h2>Инструкция для Android</h2>

                <div class="step">
                    <span>1</span>
                    <p>Откройте сайт в браузере Google Chrome.</p>
                </div>

                <div class="step">
                    <span>2</span>
                    <p>Нажмите кнопку установки ниже.</p>
                </div>

                <div class="step">
                    <span>3</span>
                    <p>Подтвердите установку приложения.</p>
                </div>

                <button v-if="showInstallButton" class="install-button" type="button" @click="installPWA">
                    Установить приложение
                </button>

                <p v-if="message" class="notice">
                    {{ message }}
                </p>
            </div>

            <div v-else class="guide desktop-guide">
                <div class="qr-box">
                    <img src="/qr-code-app.gis-inv.ru.png" alt="QR-код приложения" />

                    <p>
                        Воспользуйтесь QR-кодом для установки приложения
                        <span>"ГИС Инвентаризация"</span>
                        на мобильном устройстве
                    </p>
                </div>
            </div>
        </section>
    </main>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

type Platform = 'ios' | 'android' | 'windows' | 'mac' | 'linux' | 'other'

const emit = defineEmits<{
    close: []
}>()

const platform = ref<Platform>('other')
const isInstalled = ref(false)
const showInstallButton = ref(false)
const message = ref('')
const deferredPrompt = ref<any>(null)

async function installPWA() {
    if (!deferredPrompt.value) {
        message.value = 'Если кнопка установки не появилась, откройте сайт в Google Chrome.'
        return
    }

    deferredPrompt.value.prompt()

    const choiceResult = await deferredPrompt.value.userChoice

    if (choiceResult.outcome === 'accepted') {
        message.value = 'Приложение устанавливается.'
        showInstallButton.value = false
    } else {
        message.value = 'Установка отменена.'
    }

    deferredPrompt.value = null
}

onMounted(() => {
    const ua = navigator.userAgent

    const ios = /iPhone|iPad|iPod/i.test(ua)
    const android = /Android/i.test(ua)
    const windows = /Windows/i.test(ua)
    const mac = /Macintosh/i.test(ua) && !ios
    const linux = /Linux/i.test(ua) && !android

    platform.value = ios
        ? 'ios'
        : android
            ? 'android'
            : windows
                ? 'windows'
                : mac
                    ? 'mac'
                    : linux
                        ? 'linux'
                        : 'other'

    isInstalled.value =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true

    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault()
        deferredPrompt.value = event
        showInstallButton.value = true
    })
})
</script>

<style scoped>
.install-page {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100dvh;
    max-height: 100dvh;

    padding: 12px;

    display: flex;
    align-items: center;
    justify-content: center;

    overflow: hidden;

    background:
        radial-gradient(circle at top left, rgba(255, 95, 3, 0.12), transparent 30vw),
        linear-gradient(180deg, var(--surface) 0%, var(--surface-2) 100%);
}

.install-card {
    position: relative;

    width: min(100%, 760px);
    max-height: calc(100dvh - 24px);
    overflow: hidden;

    padding: 34px 22px 24px;
    text-align: center;

    background: var(--surface-card);
    border: 1px solid var(--border);
    border-radius: 22px;
    box-shadow: var(--shadow-lg);
    backdrop-filter: blur(18px);
}

.install-close {
    position: absolute;
    top: 10px;
    right: 10px;

    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid var(--border);
    border-radius: 10px;

    color: var(--primary);
    background: rgba(255, 255, 255, 0.65);

    font-size: 24px;
    line-height: 1;
    font-weight: 500;
    opacity: 0;
    cursor: pointer;
}

.install-close:active {
    background: rgba(255, 95, 3, 0.12);
}

.install-logo img {
    width: 100px;
    height: 100px;

    object-fit: contain;

    border-radius: 24px;
}

h1 {
    margin-bottom: 10px;

    color: var(--primary);
    font-family: var(--font-display);
    font-size: clamp(28px, 5vw, 42px);
    line-height: 1.05;
    letter-spacing: -0.05em;
}

.install-subtitle {
    max-width: 520px;
    margin: 0 auto 24px;

    color: var(--text-muted);
    font-size: 16px;
    line-height: 1.55;
}

.guide {
    text-align: left;
}

.guide h2 {
    margin-bottom: 14px;

    color: var(--primary);
    font-family: var(--font-display);
    font-size: 22px;
    letter-spacing: -0.03em;
}

.step {
    display: flex;
    gap: 12px;
    align-items: flex-start;

    padding: 13px;
    margin-bottom: 10px;

    background: rgba(255, 255, 255, 0.56);
    border: 1px solid var(--border);
    border-radius: 16px;
}

:root[data-theme='dark'] .step {
    background: rgba(255, 255, 255, 0.04);
}

.step span {
    width: 34px;
    height: 34px;
    min-width: 34px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #fff;
    background: var(--primary);
    border-radius: 12px;

    font-size: 14px;
    font-weight: 900;
}

.step p {
    color: var(--text-muted);
    font-size: 14px;
    line-height: 1.45;
}

.install-button {
    width: 100%;
    min-height: 48px;
    margin-top: 10px;

    border: 0;
    border-radius: 999px;

    color: #fff;
    background: var(--secondary);
    box-shadow: 0 16px 34px rgba(255, 95, 3, 0.26);

    font-size: 14px;
    font-weight: 900;
    cursor: pointer;
}

.notice {
    margin-top: 12px;
    padding: 12px 14px;

    color: var(--secondary-dark);
    background: rgba(255, 95, 3, 0.1);
    border: 1px solid rgba(255, 95, 3, 0.22);
    border-radius: var(--radius-sm);

    font-size: 13px;
    font-weight: 800;
    text-align: center;
}

.qr-box {
    margin-top: 10px;
    padding: 28px;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;

    background:
        linear-gradient(135deg, rgba(7, 44, 44, 0.96), rgba(10, 72, 72, 0.92));

    border-radius: 22px;
    box-shadow: var(--shadow-lg);
}

.qr-box img {
    width: 220px;
    height: 220px;

    object-fit: contain;

    padding: 14px;
    background: #fff;

    border-radius: 24px;
}

.qr-box p {
    max-width: 420px;

    color: rgba(255, 255, 255, 0.82);

    text-align: center;
    font-size: 17px;
    line-height: 1.6;
    font-weight: 700;
}

.qr-box span {
    color: #fff;
}

@media (max-width: 680px) {
    .install-page {
        padding: 10px;
    }

    .install-card {
        max-height: calc(100dvh - 20px);
        padding: 34px 14px 16px;
        border-radius: 18px;
    }

   

    .install-logo img {
       
        border-radius: 18px;
    }

    h1 {
        margin-bottom: 8px;
        font-size: 24px;
    }

    .install-subtitle {
        margin-bottom: 14px;
        font-size: 13px;
        line-height: 1.4;
    }

    .guide h2 {
        margin-bottom: 10px;
        font-size: 17px;
    }

    .step {
        gap: 10px;
        padding: 10px;
        margin-bottom: 8px;
        border-radius: 13px;
    }

    .step span {
        width: 28px;
        height: 28px;
        min-width: 28px;
        border-radius: 9px;
        font-size: 13px;
    }

    .step p {
        font-size: 13px;
        line-height: 1.35;
    }

    .notice {
        margin-top: 10px;
        padding: 10px;
        font-size: 12px;
    }

    .install-button {
        min-height: 44px;
        font-size: 13px;
    }

    .qr-box {
        margin-top: 8px;
        padding: 18px;
        gap: 14px;
        border-radius: 18px;
    }

    .qr-box img {
        width: 170px;
        height: 170px;
        padding: 10px;
        border-radius: 20px;
    }

    .qr-box p {
        font-size: 14px;
        line-height: 1.45;
    }
}
</style>